// Curated Lucide icon catalog for Work Plan staff flags (Settings → Work plan → Staff flags,
// the glyphs on planner lines/bars and the phone's flag badges).
//
// TWIN FILE: healo_app/lib/ui_kit/work_plan_flag_icons.dart carries the SAME list of names
// (mapped to LucideIcons.<camelName>). Keep the two identical by construction — a flag's
// `icon` is stored as the kebab-case Lucide name, so a name missing on either side renders
// that side's fallback glyph. Every name here is a CANONICAL lucide id (not an alias such as
// `home` → `house`) so the Flutter twin finds the same constant; each one exists in
// node_modules/lucide-react/dist/esm/icons/<name>.js and is imported statically below —
// a typo fails the build instead of shipping a blank icon. Roughly 175 entries chosen for
// staff-status semantics (shift timing, duties & access, comms, movement, people, medical,
// status) with 2–5 lowercase search keywords each; searchFlagIcons matches name + keywords.
//
// The server is permissive about icon slugs (`[a-z0-9-]{1,48}`); unknown → FLAG_ICON_FALLBACK.
import {
  AlarmClock,
  AlarmClockCheck,
  Clock,
  ClockAlert,
  Timer,
  Hourglass,
  Calendar,
  CalendarDays,
  CalendarClock,
  CalendarCheck,
  CalendarX,
  CalendarPlus,
  Sunrise,
  Sunset,
  Sun,
  Moon,
  MoonStar,
  Key,
  KeyRound,
  Lock,
  LockOpen,
  Shield,
  ShieldCheck,
  ShieldAlert,
  BadgeCheck,
  Clipboard,
  ClipboardList,
  ClipboardCheck,
  ListChecks,
  FileCheck,
  FileText,
  Receipt,
  Banknote,
  Wallet,
  CreditCard,
  Printer,
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  Headset,
  Mail,
  MailCheck,
  Megaphone,
  Bell,
  BellRing,
  BellOff,
  MessageCircle,
  MessagesSquare,
  Send,
  Car,
  Bus,
  Bike,
  TrainFront,
  Plane,
  PlaneTakeoff,
  Footprints,
  MapPin,
  MapPinCheck,
  MapPinOff,
  Navigation,
  House,
  Building,
  Building2,
  Hospital,
  Store,
  Warehouse,
  School,
  DoorOpen,
  DoorClosed,
  Luggage,
  Truck,
  Ambulance,
  User,
  UserCheck,
  UserPlus,
  UserMinus,
  UserX,
  UserCog,
  Users,
  Baby,
  GraduationCap,
  Briefcase,
  BriefcaseBusiness,
  BriefcaseMedical,
  HandHeart,
  Handshake,
  HandHelping,
  HeartHandshake,
  Crown,
  IdCard,
  Accessibility,
  Smile,
  PartyPopper,
  Stethoscope,
  Syringe,
  Pill,
  PillBottle,
  Thermometer,
  HeartPulse,
  Heart,
  Activity,
  Eye,
  EyeOff,
  Glasses,
  Microscope,
  FlaskConical,
  TestTube,
  Bandage,
  Dna,
  Brain,
  Bone,
  Ear,
  ScanEye,
  ScanFace,
  Bed,
  BedDouble,
  Biohazard,
  Droplet,
  CircleQuestionMark,
  Star,
  Sparkles,
  Flag,
  Pin,
  Award,
  Trophy,
  Check,
  CheckCheck,
  CircleCheck,
  CircleAlert,
  TriangleAlert,
  Info,
  Zap,
  ZapOff,
  Flame,
  Snowflake,
  Umbrella,
  Coffee,
  Utensils,
  UtensilsCrossed,
  Cake,
  Wrench,
  Hammer,
  Package,
  PackageCheck,
  Rocket,
  Siren,
  Wifi,
  WifiOff,
  Bot,
  Laptop,
  Monitor,
  Smartphone,
  Camera,
  Video,
  Mic,
  MicOff,
  Gift,
  Tag,
  Bookmark,
  Lightbulb,
  Target,
  Puzzle,
  Gauge,
  Leaf,
  Palette,
  Scissors,
  Trash2,
  Recycle,
  BatteryLow,
  RefreshCw,
  Repeat,
  Pause,
  Play,
  Ban,
  Settings,
  WandSparkles
} from "lucide-react";

/** Glyph used for an icon name this catalog doesn't know (and the server's empty-icon default). */
export const FLAG_ICON_FALLBACK = "flag";

function entry(name, Icon, keywords) {
  return { name, Icon, keywords };
}

// One source of truth: name ↔ component ↔ keywords. FLAG_ICON_CATALOG and ICON_COMPONENTS are
// derived views so the two can never disagree.
const CATALOG = [
  // time & shifts
  entry("alarm-clock", AlarmClock, ["wake", "early", "shift", "start", "morning"]),
  entry("alarm-clock-check", AlarmClockCheck, ["on-time", "confirmed", "shift", "punctual"]),
  entry("clock", Clock, ["time", "hours", "shift", "schedule"]),
  entry("clock-alert", ClockAlert, ["overtime", "late", "deadline", "urgent"]),
  entry("timer", Timer, ["countdown", "minutes", "short", "cover"]),
  entry("hourglass", Hourglass, ["waiting", "pending", "wait", "duration"]),
  entry("calendar", Calendar, ["date", "day", "schedule", "roster"]),
  entry("calendar-days", CalendarDays, ["week", "dates", "month", "roster"]),
  entry("calendar-clock", CalendarClock, ["scheduled", "timing", "shift", "appointment"]),
  entry("calendar-check", CalendarCheck, ["confirmed", "booked", "planned", "ok"]),
  entry("calendar-x", CalendarX, ["cancelled", "off", "absent", "no"]),
  entry("calendar-plus", CalendarPlus, ["extra", "added", "overtime", "more"]),
  entry("sunrise", Sunrise, ["morning", "early", "open", "opening", "dawn"]),
  entry("sunset", Sunset, ["evening", "late", "close", "closing", "dusk"]),
  entry("sun", Sun, ["day", "daytime", "bright", "summer"]),
  entry("moon", Moon, ["night", "late", "evening", "sleep"]),
  entry("moon-star", MoonStar, ["night", "late", "evening", "stay", "close"]),
  // duties & access
  entry("key", Key, ["keys", "keyholder", "unlock", "open", "access"]),
  entry("key-round", KeyRound, ["keys", "keyholder", "unlock", "access"]),
  entry("lock", Lock, ["locked", "secure", "close", "lockup"]),
  entry("lock-open", LockOpen, ["unlocked", "open", "opening", "access"]),
  entry("shield", Shield, ["protect", "safety", "security", "guard"]),
  entry("shield-check", ShieldCheck, ["verified", "safe", "checked", "trusted"]),
  entry("shield-alert", ShieldAlert, ["risk", "warning", "safety", "alert"]),
  entry("badge-check", BadgeCheck, ["verified", "certified", "approved", "checked"]),
  entry("clipboard", Clipboard, ["notes", "tasks", "list", "checklist"]),
  entry("clipboard-list", ClipboardList, ["tasks", "checklist", "duties", "todo"]),
  entry("clipboard-check", ClipboardCheck, ["done", "completed", "checklist", "verified"]),
  entry("list-checks", ListChecks, ["checklist", "tasks", "done", "duties"]),
  entry("file-check", FileCheck, ["approved", "document", "signed", "form"]),
  entry("file-text", FileText, ["document", "report", "paper", "notes"]),
  entry("receipt", Receipt, ["payment", "bill", "cash", "receipt"]),
  entry("banknote", Banknote, ["cash", "money", "payment", "float"]),
  entry("wallet", Wallet, ["cash", "money", "payment", "float"]),
  entry("credit-card", CreditCard, ["card", "payment", "terminal", "pos"]),
  entry("printer", Printer, ["print", "paper", "forms", "labels"]),
  // communications
  entry("phone", Phone, ["call", "phones", "reception", "answer"]),
  entry("phone-call", PhoneCall, ["calling", "call", "dial", "reception"]),
  entry("phone-incoming", PhoneIncoming, ["incoming", "calls", "answer", "reception"]),
  entry("phone-off", PhoneOff, ["no-calls", "silent", "unavailable", "dnd"]),
  entry("headset", Headset, ["support", "call-centre", "hotline", "calls"]),
  entry("mail", Mail, ["email", "post", "letters", "inbox"]),
  entry("mail-check", MailCheck, ["replied", "sent", "done", "email"]),
  entry("megaphone", Megaphone, ["announce", "broadcast", "notice", "shout"]),
  entry("bell", Bell, ["alert", "notify", "reminder", "ring"]),
  entry("bell-ring", BellRing, ["alarm", "ringing", "notify", "urgent"]),
  entry("bell-off", BellOff, ["silent", "muted", "quiet", "dnd"]),
  entry("message-circle", MessageCircle, ["chat", "message", "whatsapp", "reply"]),
  entry("messages-square", MessagesSquare, ["chats", "messages", "conversation", "group"]),
  entry("send", Send, ["send", "dispatch", "submit", "deliver"]),
  // movement & places
  entry("car", Car, ["drive", "driving", "vehicle", "transport"]),
  entry("bus", Bus, ["bus", "transport", "commute", "shuttle"]),
  entry("bike", Bike, ["bicycle", "cycle", "ride", "commute"]),
  entry("train-front", TrainFront, ["train", "mrt", "commute", "rail"]),
  entry("plane", Plane, ["flight", "travel", "abroad", "trip"]),
  entry("plane-takeoff", PlaneTakeoff, ["departure", "leaving", "flight", "travel"]),
  entry("footprints", Footprints, ["walk", "walking", "steps", "rounds"]),
  entry("map-pin", MapPin, ["location", "place", "site", "clinic"]),
  entry("map-pin-check", MapPinCheck, ["arrived", "on-site", "present", "location"]),
  entry("map-pin-off", MapPinOff, ["off-site", "away", "remote", "absent"]),
  entry("navigation", Navigation, ["direction", "route", "travel", "gps"]),
  entry("house", House, ["home", "wfh", "remote", "house"]),
  entry("building", Building, ["office", "branch", "site", "premises"]),
  entry("building-2", Building2, ["office", "hq", "branch", "site"]),
  entry("hospital", Hospital, ["hospital", "clinic", "medical", "ward"]),
  entry("store", Store, ["shop", "front", "retail", "outlet"]),
  entry("warehouse", Warehouse, ["stock", "storage", "store", "supplies"]),
  entry("school", School, ["school", "training", "campus", "education"]),
  entry("door-open", DoorOpen, ["opening", "open", "entrance", "arrive"]),
  entry("door-closed", DoorClosed, ["closing", "closed", "exit", "leave"]),
  entry("luggage", Luggage, ["travel", "trip", "bags", "away"]),
  entry("truck", Truck, ["delivery", "van", "transport", "logistics"]),
  entry("ambulance", Ambulance, ["emergency", "ambulance", "transfer", "urgent"]),
  // people & roles
  entry("user", User, ["person", "staff", "member", "individual"]),
  entry("user-check", UserCheck, ["approved", "present", "confirmed", "attendance"]),
  entry("user-plus", UserPlus, ["new", "joiner", "onboarding", "added"]),
  entry("user-minus", UserMinus, ["removed", "leaving", "offboard", "less"]),
  entry("user-x", UserX, ["absent", "away", "removed", "missing"]),
  entry("user-cog", UserCog, ["manager", "admin", "settings", "supervisor"]),
  entry("users", Users, ["team", "group", "staff", "people"]),
  entry("baby", Baby, ["baby", "infant", "paediatric", "child"]),
  entry("graduation-cap", GraduationCap, ["trainee", "student", "learning", "intern"]),
  entry("briefcase", Briefcase, ["work", "business", "duty", "job"]),
  entry("briefcase-business", BriefcaseBusiness, ["work", "manager", "business", "office"]),
  entry("briefcase-medical", BriefcaseMedical, ["medical", "kit", "first-aid", "doctor"]),
  entry("hand-heart", HandHeart, ["care", "kindness", "volunteer", "support"]),
  entry("handshake", Handshake, ["agreement", "partner", "welcome", "deal"]),
  entry("hand-helping", HandHelping, ["help", "assist", "support", "cover"]),
  entry("heart-handshake", HeartHandshake, ["care", "partner", "trust", "support"]),
  entry("crown", Crown, ["lead", "leader", "senior", "in-charge"]),
  entry("id-card", IdCard, ["id", "badge", "identity", "pass"]),
  entry("accessibility", Accessibility, ["accessible", "wheelchair", "disability", "access"]),
  entry("smile", Smile, ["happy", "friendly", "smile", "good"]),
  entry("party-popper", PartyPopper, ["celebration", "party", "birthday", "event"]),
  // medical
  entry("stethoscope", Stethoscope, ["doctor", "clinical", "exam", "medical"]),
  entry("syringe", Syringe, ["injection", "vaccine", "jab", "needle"]),
  entry("pill", Pill, ["medicine", "tablet", "pharmacy", "drug"]),
  entry("pill-bottle", PillBottle, ["medicine", "pharmacy", "bottle", "dispense"]),
  entry("thermometer", Thermometer, ["temperature", "fever", "screening", "heat"]),
  entry("heart-pulse", HeartPulse, ["vitals", "pulse", "cardio", "health"]),
  entry("heart", Heart, ["heart", "care", "love", "health"]),
  entry("activity", Activity, ["vitals", "monitor", "pulse", "activity"]),
  entry("eye", Eye, ["eye", "vision", "optom", "look"]),
  entry("eye-off", EyeOff, ["hidden", "blind", "unseen", "off"]),
  entry("glasses", Glasses, ["glasses", "optical", "spectacles", "optom"]),
  entry("microscope", Microscope, ["lab", "laboratory", "research", "analysis"]),
  entry("flask-conical", FlaskConical, ["lab", "chemistry", "sample", "test"]),
  entry("test-tube", TestTube, ["sample", "lab", "blood", "test"]),
  entry("bandage", Bandage, ["first-aid", "wound", "dressing", "injury"]),
  entry("dna", Dna, ["genetics", "lab", "dna", "research"]),
  entry("brain", Brain, ["mental", "brain", "neurology", "mind"]),
  entry("bone", Bone, ["bone", "ortho", "fracture", "skeleton"]),
  entry("ear", Ear, ["hearing", "ear", "audio", "ent"]),
  entry("scan-eye", ScanEye, ["eye-scan", "retina", "vision", "screening"]),
  entry("scan-face", ScanFace, ["face", "identity", "scan", "recognition"]),
  entry("bed", Bed, ["bed", "rest", "inpatient", "sleep"]),
  entry("bed-double", BedDouble, ["beds", "ward", "room", "inpatient"]),
  entry("biohazard", Biohazard, ["hazard", "infectious", "danger", "biohazard"]),
  entry("droplet", Droplet, ["blood", "drop", "fluid", "water"]),
  // status & misc
  entry("circle-question-mark", CircleQuestionMark, ["question", "help", "unsure", "unknown"]),
  entry("star", Star, ["star", "favourite", "top", "best"]),
  entry("sparkles", Sparkles, ["new", "special", "shine", "magic"]),
  entry("flag", Flag, ["flag", "marker", "note", "attention"]),
  entry("pin", Pin, ["pinned", "fixed", "stay", "hold"]),
  entry("award", Award, ["award", "honour", "prize", "recognition"]),
  entry("trophy", Trophy, ["trophy", "winner", "champion", "prize"]),
  entry("check", Check, ["done", "ok", "yes", "complete"]),
  entry("check-check", CheckCheck, ["double", "confirmed", "all-done", "verified"]),
  entry("circle-check", CircleCheck, ["done", "ok", "complete", "approved"]),
  entry("circle-alert", CircleAlert, ["alert", "attention", "warning", "notice"]),
  entry("triangle-alert", TriangleAlert, ["warning", "caution", "danger", "alert"]),
  entry("info", Info, ["info", "information", "note", "details"]),
  entry("zap", Zap, ["fast", "energy", "quick", "urgent"]),
  entry("zap-off", ZapOff, ["no-power", "outage", "off", "down"]),
  entry("flame", Flame, ["hot", "urgent", "fire", "priority"]),
  entry("snowflake", Snowflake, ["cold", "freeze", "winter", "aircon"]),
  entry("umbrella", Umbrella, ["rain", "cover", "backup", "weather"]),
  entry("coffee", Coffee, ["break", "coffee", "tea", "pause"]),
  entry("utensils", Utensils, ["lunch", "meal", "food", "dinner"]),
  entry("utensils-crossed", UtensilsCrossed, ["lunch", "meal", "food", "eat"]),
  entry("cake", Cake, ["birthday", "celebration", "cake", "party"]),
  entry("wrench", Wrench, ["fix", "maintenance", "repair", "tools"]),
  entry("hammer", Hammer, ["build", "repair", "tools", "fix"]),
  entry("package", Package, ["parcel", "delivery", "stock", "box"]),
  entry("package-check", PackageCheck, ["delivered", "received", "stock", "done"]),
  entry("rocket", Rocket, ["launch", "fast", "new", "go"]),
  entry("siren", Siren, ["emergency", "alarm", "urgent", "siren"]),
  entry("wifi", Wifi, ["online", "internet", "network", "connected"]),
  entry("wifi-off", WifiOff, ["offline", "no-internet", "disconnected", "down"]),
  entry("bot", Bot, ["bot", "ai", "robot", "automation"]),
  entry("laptop", Laptop, ["laptop", "computer", "it", "remote"]),
  entry("monitor", Monitor, ["screen", "computer", "desk", "it"]),
  entry("smartphone", Smartphone, ["mobile", "phone", "app", "device"]),
  entry("camera", Camera, ["photo", "camera", "picture", "snap"]),
  entry("video", Video, ["video", "call", "meeting", "zoom"]),
  entry("mic", Mic, ["microphone", "speak", "record", "audio"]),
  entry("mic-off", MicOff, ["muted", "silent", "mic", "off"]),
  entry("gift", Gift, ["gift", "present", "reward", "bonus"]),
  entry("tag", Tag, ["tag", "label", "price", "marker"]),
  entry("bookmark", Bookmark, ["bookmark", "saved", "mark", "keep"]),
  entry("lightbulb", Lightbulb, ["idea", "tip", "insight", "light"]),
  entry("target", Target, ["target", "goal", "focus", "aim"]),
  entry("puzzle", Puzzle, ["puzzle", "piece", "fit", "problem"]),
  entry("gauge", Gauge, ["speed", "meter", "performance", "level"]),
  entry("leaf", Leaf, ["eco", "green", "nature", "leaf"]),
  entry("palette", Palette, ["colour", "design", "art", "palette"]),
  entry("scissors", Scissors, ["cut", "scissors", "trim", "snip"]),
  entry("trash-2", Trash2, ["bin", "rubbish", "discard", "trash"]),
  entry("recycle", Recycle, ["recycle", "reuse", "green", "waste"]),
  entry("battery-low", BatteryLow, ["low", "battery", "tired", "drained"]),
  entry("refresh-cw", RefreshCw, ["refresh", "reload", "again", "sync"]),
  entry("repeat", Repeat, ["repeat", "again", "recurring", "loop"]),
  entry("pause", Pause, ["pause", "hold", "wait", "break"]),
  entry("play", Play, ["play", "start", "go", "begin"]),
  entry("ban", Ban, ["banned", "blocked", "no", "forbidden"]),
  entry("settings", Settings, ["settings", "config", "gear", "setup"]),
  entry("wand-sparkles", WandSparkles, ["magic", "wand", "auto", "special"]),
];

/** [{name, keywords}] in display order — what the icon picker renders. */
export const FLAG_ICON_CATALOG = Object.freeze(
  CATALOG.map(({ name, keywords }) => Object.freeze({ name, keywords: Object.freeze([...keywords]) })),
);

/** name → lucide-react component (static imports, tree-shakeable). */
export const ICON_COMPONENTS = Object.freeze(Object.fromEntries(CATALOG.map(({ name, Icon }) => [name, Icon])));

function normalizeName(name) {
  return String(name || "").trim();
}

export function isKnownFlagIcon(name) {
  return Object.prototype.hasOwnProperty.call(ICON_COMPONENTS, normalizeName(name));
}

/** The component for an icon name, falling back to the flag glyph for unknown/blank names. */
export function resolveFlagIcon(name) {
  return ICON_COMPONENTS[normalizeName(name)] || ICON_COMPONENTS[FLAG_ICON_FALLBACK];
}

/**
 * Catalog entries matching a free-text query: every whitespace-separated token must appear
 * in the name or one of the keywords (case-insensitive). An empty query returns everything.
 */
export function searchFlagIcons(query) {
  const tokens = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
  if (tokens.length === 0) return FLAG_ICON_CATALOG;
  return FLAG_ICON_CATALOG.filter((item) =>
    tokens.every((token) => item.name.includes(token) || item.keywords.some((keyword) => keyword.includes(token))),
  );
}
